import React, { useState } from 'react';
import { PageHeader, SpecStrip, Stat, SectionHeader, InkPanel, Callout, RedNote, EmptyState, TYPE, cn } from './ui';
import { Expense, Income, CreditCard, PayoffPlan, Loan } from '../types';
import { buildLedger, thresholdBreaches } from '../utils/calculations';
import { projectCardBalance } from '../utils/finance/cardLedger';
import { addDays, startOfDay, today as todayFn } from '../utils/finance/dates';
import { backupApi } from '../services/api';

interface DashboardProps {
  currentBalance: number;
  expenses: Expense[];
  incomes: Income[];
  creditCards: CreditCard[];
  plans: PayoffPlan[];
  loans?: Loan[];
  lowBalanceThreshold?: number;
}

export const Dashboard: React.FC<DashboardProps> = ({
  currentBalance,
  expenses,
  incomes,
  creditCards,
  plans,
  loans = [],
  lowBalanceThreshold = 0
}) => {
  const today = new Date();
  const [backupStatus, setBackupStatus] = useState<{
    isLoading: boolean;
    message: string;
    isSuccess: boolean;
  }>({ isLoading: false, message: '', isSuccess: false });

  const handleBackup = async () => {
    setBackupStatus({ isLoading: true, message: 'Creating backup...', isSuccess: false });
    
    try {
      const result = await backupApi.createBackup();
      console.log('Backup result:', result); // Debug log
      
      setBackupStatus({
        isLoading: false,
        message: result.message || 'Backup completed successfully',
        isSuccess: true
      });
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setBackupStatus({ isLoading: false, message: '', isSuccess: false });
      }, 5000);
      
    } catch (error: any) {
      console.error('Backup error:', error); // Debug log
      
      let errorMessage = 'Backup failed';
      if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.error) {
        errorMessage = error.error;
      }
      
      setBackupStatus({
        isLoading: false,
        message: errorMessage,
        isSuccess: false
      });
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setBackupStatus({ isLoading: false, message: '', isSuccess: false });
      }, 5000);
    }
  };
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const ledgerBase = {
    openingBalance: currentBalance,
    openingDate: todayFn(),
    expenses,
    incomes,
    loans,
    creditCards,
    plans,
    lowBalanceThreshold,
  };

  // Forward 90 days. One ledger serves the 90-day metric, the end-of-month
  // metric and the threshold banner.
  const forwardDays = React.useMemo(
    () => buildLedger({ ...ledgerBase, from: todayFn(), to: addDays(todayFn(), 90) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentBalance, expenses, incomes, loans, creditCards, plans, lowBalanceThreshold]
  );

  const ninetyDayBalance = forwardDays.length
    ? forwardDays[forwardDays.length - 1].balance
    : currentBalance;
  const endOfMonthBalance =
    forwardDays.find(d => d.date.getTime() === startOfDay(endOfMonth).getTime())?.balance ??
    ninetyDayBalance;

  const breaches = thresholdBreaches(forwardDays);

  // Month-to-date. Previously ~150 lines that reimplemented recurrence
  // matching and credit-card payment selection a fourth time; now it's a
  // query over the same ledger every other view uses, so the numbers agree.
  const monthToDate = React.useMemo(
    () => buildLedger({ ...ledgerBase, from: startOfDay(startOfMonth), to: todayFn() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentBalance, expenses, incomes, loans, creditCards, plans, startOfMonth.getTime()]
  );

  const mtdEvents = monthToDate.flatMap(d => d.events);
  const totalIncomeThisMonth = mtdEvents
    .filter(e => e.type === 'income')
    .reduce((sum, e) => sum + e.amount, 0);
  const totalSpendThisMonth = mtdEvents
    .filter(e => e.type === 'expense')
    .reduce((sum, e) => sum + e.amount, 0);
  // Projected, not the stored anchor — matches Credit Cards and Debt Sheet,
  // which previously disagreed with this tile.
  const totalDebt = creditCards.reduce((sum, card) => sum + projectCardBalance(card, plans), 0);

  const spendingByCategory = mtdEvents.reduce((categories, e) => {
    if (e.type !== 'expense') return categories;
    const label =
      e.source.kind === 'cardPayment'
        ? 'Credit Card Payments'
        : e.source.kind === 'loan'
          ? 'Loan Payments'
          : e.source.kind === 'expense'
            ? e.source.category || 'Other'
            : 'Other';
    categories[label] = (categories[label] || 0) + e.amount;
    return categories;
  }, {} as Record<string, number>);

  // Sort categories by amount (descending)
  const sortedCategories = Object.entries(spendingByCategory).sort((a, b) => b[1] - a[1]);

  const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
  const softBreaches = breaches.filter(b => !b.isNegative);
  const activePlans = plans.filter(pl => pl.status === 'ACTIVE' && pl.mode !== 'LUMP_SUM').length;

  return (
    <>
      <PageHeader
        heading="Financial Dashboard"
        subhead="Where the money stands this month, and where it is heading."
      />

      {/* Facts on the left, the figures that matter on the right. Red is spent
          on the one thing that needs attention, not on every negative number. */}
      <SpecStrip
        className="mt-11"
        facts={[
          <>Month to date</>,
          <>{monthToDate.length} days</>,
          <>{creditCards.length} cards</>,
          <>{activePlans} active {activePlans === 1 ? 'plan' : 'plans'}</>,
        ]}
      >
        <Stat value={usd(totalIncomeThisMonth)} label="Income MTD" />
        <Stat value={usd(totalSpendThisMonth)} label="Spend MTD" />
        <Stat value={usd(currentBalance)} label="Current balance" accent={currentBalance < 0} />
        <Stat value={usd(endOfMonthBalance)} label="End of month" accent={endOfMonthBalance < 0} />
        <Stat value={usd(ninetyDayBalance)} label="90-day projection" accent={ninetyDayBalance < 0} />
      </SpecStrip>

      {/* Alerts. At most one is shown — they are ordered by severity, and
          stacking three warnings would spend red on everything. */}
      {currentBalance < 0 ? (
        <Callout className="mt-11">
          <div className={cn(TYPE.metaTight, 'text-bad')}>Negative balance</div>
          <p className={cn(TYPE.body, 'text-ink-soft mt-2')}>
            Your current balance is below zero. Act now to avoid overdraft fees.
          </p>
        </Callout>
      ) : ninetyDayBalance < 0 ? (
        <Callout className="mt-11">
          <div className={cn(TYPE.metaTight, 'text-bad')}>Projected negative</div>
          <p className={cn(TYPE.body, 'text-ink-soft mt-2')}>
            Your balance is projected to go negative within 90 days. Consider adjusting spending.
          </p>
        </Callout>
      ) : lowBalanceThreshold > 0 && softBreaches.length > 0 ? (
        <RedNote className="mt-11">
          <div className={cn(TYPE.rowTitle, 'text-ink')}>
            Low balance ahead — {softBreaches.length} {softBreaches.length === 1 ? 'day' : 'days'} below{' '}
            {usd(lowBalanceThreshold)}
          </div>
          <p className={cn(TYPE.small, 'text-ink-soft mt-2')}>
            {(() => {
              const lowest = breaches.reduce((min, b) => (b.balance < min.balance ? b : min), breaches[0]);
              return `First on ${breaches[0].date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}; lowest is ${usd(lowest.balance)} on ${lowest.date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}.`;
            })()}
          </p>
        </RedNote>
      ) : null}

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-x-14 gap-y-12">
        <section>
          <SectionHeader label="Spend by category" note={`${sortedCategories.length} categories`} />
          {sortedCategories.length === 0 ? (
            <EmptyState>Nothing spent yet this month</EmptyState>
          ) : (
            <div className="bg-surface border border-rule-base rounded-xl overflow-hidden mt-5">
              {sortedCategories.map(([category, amount]) => (
                <div
                  key={category}
                  className="flex items-baseline justify-between gap-6 px-4 py-[14px] border-b border-rule last:border-b-0"
                >
                  <span className={cn(TYPE.rowTitle, 'text-ink min-w-0 truncate')}>{category}</span>
                  <span className={cn(TYPE.rowFigure, "text-ink shrink-0")}>
                    {usd(amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-baseline justify-between gap-6 pt-4">
            <span className={cn(TYPE.metaTight, 'text-ink-muted')}>Total spend this month</span>
            <span className={cn(TYPE.bigNumber, 'text-ink')}>{usd(totalSpendThisMonth)}</span>
          </div>
        </section>

        <section>
          <SectionHeader label="Debt" note={creditCards.length > 0 ? `${creditCards.length} cards` : undefined} />
          {creditCards.length === 0 ? (
            <EmptyState>No credit cards on file</EmptyState>
          ) : (
            <>
              <div className="flex flex-wrap gap-x-10 gap-y-5 mt-6">
                <Stat value={usd(totalDebt)} label="Card debt" />
                <Stat value={activePlans} label="Active plans" />
              </div>
              {/* The one ink panel on this page: the single most opinionated
                  piece of guidance it has to offer. */}
              <InkPanel label="Where this is going" className="mt-8">
                {activePlans === 0
                  ? 'No payoff plan is running. Cards are projected at their computed minimum, which is the slowest and most expensive way to clear them.'
                  : `${activePlans} ${activePlans === 1 ? 'card is' : 'cards are'} on a payoff plan. Everything else is projected at the computed minimum.`}
              </InkPanel>
            </>
          )}
        </section>
      </div>
    </>
  );
};